import { useMemo } from 'react';
import useFetchInternalCustomDataFields from 'components/domain/customData/fields/internal/useFetchInternalCustomDataFields';
import {
  CustomDataFieldUsage,
  InternalCustomDataFieldSubType,
} from 'models/CustomDataField';
import {
  DisplayPreArrivalFormField,
  InformationMap,
  PreArrivalFormFields,
} from './PreArrivalFormTab.types';
import {
  DEFAULT_FIELDS_PRIMARY_GUEST,
  DEFAULT_FIELDS_OTHER_GUEST,
  DEFAULT_FIELDS_ARRIVAL_INFORMATION,
  DEFAULT_FIELDS_OTHER,
  DefaultFieldsMap,
} from './PreArrivalFormTab.constants';

const getIdBasedOnNameAndSubType = (
  name: string,
  subType: InternalCustomDataFieldSubType,
): PreArrivalFormFields => {
  const fields = {
    [InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION]:
      DEFAULT_FIELDS_PRIMARY_GUEST,
    [InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION]:
      DEFAULT_FIELDS_OTHER_GUEST,
    [InternalCustomDataFieldSubType.ARRIVAL_INFORMATION]:
      DEFAULT_FIELDS_ARRIVAL_INFORMATION,
    [InternalCustomDataFieldSubType.OTHER]: DEFAULT_FIELDS_OTHER,
  };

  return Object.entries(fields[subType]).find(
    ([, field]) => field.name === name,
  )?.[0] as PreArrivalFormFields;
};

/**
 * This hook is used to render the table and fill the form's defaults.
 *
 * It also contains a logic that gets static data from a file and combines with the dynamic data from the database.
 */
const usePreArrivalFormMap = () => {
  const { data: preArrivalFormFields } = useFetchInternalCustomDataFields({
    usage: CustomDataFieldUsage.PRE_ARRIVAL_FORM,
  });

  const preArrivalFormMap = useMemo(() => {
    const backendDataMap = (
      preArrivalFormFields?.customDataFields || []
    ).reduce(
      (acc, field) => {
        const id = getIdBasedOnNameAndSubType(field.name, field.subType);

        if (id) {
          acc.default[id] = {
            id,
            ...field,
            validation: field.isOptional ? 'OPTIONAL' : 'REQUIRED',
          };
        }

        // this is what caracterizes a custom field:
        // it couldn't be mapped before and subType is OTHER
        if (field.subType === InternalCustomDataFieldSubType.OTHER && !id) {
          acc.custom[field.uid!] = {
            id: PreArrivalFormFields.CUSTOM,
            ...field,
            validation: field.isOptional ? 'OPTIONAL' : 'REQUIRED',
          };
        }

        return acc;
      },
      {
        custom: {},
        default: {},
      } as unknown as {
        default: InformationMap;
        custom: Record<string, DisplayPreArrivalFormField>;
      },
    );

    const getMap = (defaultFields: DefaultFieldsMap) =>
      Object.entries(defaultFields).reduce((acc, [key, value]) => {
        const keyAsPreArrivalFormFields = key as PreArrivalFormFields;
        if (backendDataMap.default[keyAsPreArrivalFormFields]) {
          acc[keyAsPreArrivalFormFields] =
            backendDataMap.default[keyAsPreArrivalFormFields];
        } else {
          acc[keyAsPreArrivalFormFields] = {
            id: keyAsPreArrivalFormFields,
            ...value,
            validation: value.isOptional ? 'OPTIONAL' : 'REQUIRED',
          };
        }
        return acc;
      }, {} as InformationMap);

    const primaryGuestInformationMap = getMap(DEFAULT_FIELDS_PRIMARY_GUEST);
    const otherGuestInformationMap = getMap(DEFAULT_FIELDS_OTHER_GUEST);
    const arrivalInformationMap = getMap(DEFAULT_FIELDS_ARRIVAL_INFORMATION);
    const otherInformationMap = getMap(DEFAULT_FIELDS_OTHER);
    const customFieldsMap = backendDataMap.custom;

    return {
      primaryGuestInformationMap,
      otherGuestInformationMap,
      arrivalInformationMap,
      otherInformationMap,
      customFieldsMap,
    };
  }, [preArrivalFormFields?.customDataFields]);

  return preArrivalFormMap;
};

export default usePreArrivalFormMap;
